/**
 * VideoPlayer Component
 * HTML5 video player modal with native controls
 *
 * Plays clips as if they were standalone videos: a clip shares its source's file, so the
 * player seeks to the clip's start on load and stops at its end.
 */

import { useState, useEffect } from 'react';
import { useClipPlayback } from '../../../../../hooks/useClipPlayback';
import { getValidatedGcsUrl } from '../../../../../utils/url-validation';
import { Button } from '../../../../../components/Button';
import { VideoDownloadButton } from '../../../../../components/VideoDownloadButton';
import './VideoPlayer.css';

interface VideoPlayerProps {
  videoId: string;
  getVideoUrl: (videoId: string) => Promise<string | null>;
  onClose: () => void;
  /** Set for clips: playback is confined to this window of the source file. */
  clipStartSeconds?: number | null;
  clipEndSeconds?: number | null;
  /** Offers "Notify a coach" about this video. Omitted where tagging is not offered. */
  onNotify?: () => void;
  /** Offers "Download". Omitted where the prospect is not known. */
  prospectId?: string;
}

export function VideoPlayer({
  videoId,
  getVideoUrl,
  onClose,
  clipStartSeconds,
  clipEndSeconds,
  onNotify,
  prospectId,
}: VideoPlayerProps) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { videoRef, isClip, handlers } = useClipPlayback(clipStartSeconds, clipEndSeconds);

  useEffect(() => {
    const loadVideo = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const url = await getVideoUrl(videoId);

        // Validate URL before setting
        const validatedUrl = getValidatedGcsUrl(url);
        if (!validatedUrl) {
          console.error('[VideoPlayer] Invalid or non-GCS URL', {
            videoId,
            receivedUrl: url,
          });
          setError('Invalid video URL. Please contact support.');
        } else {
          setVideoUrl(validatedUrl);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load video');
      } finally {
        setIsLoading(false);
      }
    };

    loadVideo();
  }, [videoId, getVideoUrl]);

  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="video-player__overlay" onClick={onClose}>
      <div className="video-player__container" onClick={(e) => e.stopPropagation()}>
        {isLoading && <div className="video-player__loading">Loading video...</div>}
        {error && <div className="video-player__error">{error}</div>}
        {videoUrl && !isLoading && !error && (
          <video
            ref={videoRef}
            className="video-player__video"
            // Clips start at an offset into a shared file, so the browser must fetch metadata
            // before it can seek. Without this, Safari can start at 0 and play the wrong part.
            preload={isClip ? 'auto' : undefined}
            src={videoUrl}
            controls
            autoPlay
            onLoadedMetadata={handlers.onLoadedMetadata}
            onTimeUpdate={handlers.onTimeUpdate}
            onSeeking={handlers.onSeeking}
            onError={() => setError('Unable to play video')}
          />
        )}
        {(onNotify || prospectId) && (
          <div className="video-player__actions">
            {prospectId && (
              <VideoDownloadButton
                key={videoId}
                prospectId={prospectId}
                videoId={videoId}
                isClip={isClip}
                showClipHint={false}
              />
            )}
            {onNotify && (
              <Button variant="secondary" onClick={onNotify}>
                Notify a coach
              </Button>
            )}
          </div>
        )}
        <button className="video-player__close" onClick={onClose} aria-label="Close video player">
          ×
        </button>
      </div>
    </div>
  );
}
